import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {LIVING_LAVA_KIND} from '../../../configs/biomeEffects/livingLava';
import {
  TWO_PI,
  expandBox,
  lerp,
  mixTints,
  pick,
  pickWeighted,
  randomIn,
  randomInt,
  randomRange,
  randomSign,
  resolveLifeEnvelope,
  smoothstep,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * ЖИВАЯ ЛАВА (подложка пекла). Чистые функции без Phaser и DOM: по этому
 * описанию частицы каждый кадр ставит сцена (scenes/FieldBiomeEffects).
 *
 * Пять дорожек:
 *  - волна жара: группа — одна волна, бегущая по одному нарисованному руслу
 *    лавы. По светящемуся пятну на каждый якорь русла, на земле; сосед по руслу
 *    разгорается чуть позже предыдущего, и глаз видит, как жар течет по реке.
 *    На гребне волны участок русла может плюнуть горстью сгустков магмы (жерла
 *    и озера — всегда). Лава, на которую легли игровые ячейки, только светится:
 *    сгустки летят над жетонами и вылетали бы из-под строений. Русло выбирается
 *    из тех, что сейчас не горят, охотнее — из тех, что видит камера;
 *  - залпы вулканов: группа — один залп одного кратера: вспышка жерла и горсть
 *    сгустков, выше и гуще всплеска русла. Кратер — тот, что сейчас молчит,
 *    охотнее — тот, что видит камера;
 *  - дым кратеров: группа — один клуб. Клубы поднимаются над кратером вверх по
 *    экрану, разбухая и тая, и слегка клонятся по ветру матча;
 *  - свет жерл: постоянная группа — зарево и ядро в каждом кратере дышат неровно;
 *  - огоньки гор: группа — одна «проснувшаяся» гора: несколько огоньков на
 *    магме ее жетона проступают, медленно дышат и гаснут. Больше с жетона горы
 *    ничего не родится: сгустками горы не плюются — так решил владелец игры.
 *
 * СГУСТОК МАГМЫ летит как брошенный камень: вбок — ровно, по вертикали — рывок
 * вверх против постоянного тяготения. Отсюда и вес: быстрый вылет, торможение,
 * зависание в вершине и разгон вниз. Сгусток — пара частиц с общим полетом:
 * плотная капля (обычное наложение) и ореол накала (светится). Капля остывает
 * от бело-желтой до красной в вершине, на пути вниз застывает в темно-серую и
 * только потом тает; ореол гаснет к мигу, когда капля застыла.
 *
 * Горы поля читаются при рождении каждой группы (context.mountains): их ставят
 * и убирают, пока эффект жив. Нет гор — дорожка огоньков молчит.
 */

const HEAT = 'heat';
const CLOT_BODY = 'clotBody';
const CLOT_HALO = 'clotHalo';
const FLARE = 'flare';
const SMOKE = 'smoke';
const CRATER_GLOW = 'craterGlow';
const MOUNTAIN_GLOW = 'mountainGlow';
// Свет огонька на горе — два пятна: ореол снизу, сердцевина поверх
const GLOW_PARTS = ['halo', 'core'];

// Слои того, что лежит на земле — под дымкой пустых ячеек и под жетонами: жар
// русел и свет кратеров (светятся), дым (обычное наложение)
const GROUND_GLOW_LAYER = FIELD_BIOME_LAYER_KEYS.GROUND_GLOW;
const GROUND_LAYER = FIELD_BIOME_LAYER_KEYS.GROUND;
// Порядок внутри слоя: ядро света жерла и вспышка — над заревом; капля сгустка —
// над дымом, ореол сгустка — над всяким светом своего слоя
const CORE_SUBLAYER = 1;
const CLOT_BODY_SUBLAYER = 1;
const CLOT_HALO_SUBLAYER = 2;

const checkIfPointInBox = ({x, y}, box) => x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;
const resolveMountainKey = ({x, y}) => `${x}:${y}`;

/**
 * Краска сгустка. На подъеме остывает: бело-желтая -> оранжевая -> красная в
 * вершине. На пути вниз застывает: красная -> темно-серая.
 *
 * @param {Object} clots — числа сгустков
 * @param {number} riseShare — доля подъема, 0..1
 * @param {number} fallShare — доля падения, 0..1 (0 — сгусток еще поднимается)
 */
const resolveClotTint = ({tints, warmAt, solidAt}, riseShare, fallShare) => {
  if (fallShare > 0) {
    return mixTints(tints.red, tints.solid, fallShare / solidAt);
  }
  return riseShare < warmAt
    ? mixTints(tints.hot, tints.warm, riseShare / warmAt)
    : mixTints(tints.warm, tints.red, (riseShare - warmAt) / (1 - warmAt));
};

/** Вспышка: разгорается рывком и гаснет; пятно вырастает от startScale своего размера */
const resolveFlarePose = (particle, elapsedMs, out) => {
  const ageMs = elapsedMs - particle.startMs;
  const flash = smoothstep(0, particle.riseMs, ageMs)
    * (1 - smoothstep(particle.riseMs, particle.riseMs + particle.decayMs, ageMs));
  if (!(flash > 0)) {
    return writeHiddenPose(out);
  }
  const size = particle.size * lerp(particle.startScale, 1, flash);
  return writePose(out, particle.x, particle.y, particle.alpha * flash, size, size * particle.flatten, 0);
};

const createTracks = (context, params) => {
  const {hexSize, side, wind} = context;
  const {pulse, glow, clots, splashes, craters, mountains} = params;
  // Тяготение в точках мира на миллисекунду в квадрате
  const gravity = (hexSize * clots.gravityFactor) / 1e6;

  // ------------------------------------------------------------------ сгустки

  /**
   * Сгустки одного выброса. Каждый — пара частиц (капля и ореол) с общим
   * полетом.
   *
   * @param {Function} rng
   * @param {Object} burst
   * @param {number} burst.x, burst.y — откуда выброс
   * @param {Object} burst.look — числа вида выброса: всплеск русла, залп вулкана
   * @param {number} burst.count — сколько сгустков
   * @param {number} burst.startMs — когда выброс начинается (от рождения группы)
   * @param {number} burst.ceilingY — выше этой линии мира сгусткам нельзя
   * @param {number} [burst.scale] — множитель размера и высоты
   * @param {number} [burst.spreadX], [burst.spreadY] — разброс мест вылета
   * @returns {{particles: Object[], endMs: number}} endMs — когда упадет последний
   */
  const buildClots = (rng, {x, y, look, count, startMs, ceilingY, scale = 1, spreadX = 0, spreadY = 0}) => {
    const particles = [];
    let endMs = startMs;
    for (let index = 0; index < count; index += 1) {
      // Доля размера: 0 — самый мелкий сгусток выброса, 1 — самый крупный
      const sizeShare = rng();
      const startX = x + randomRange(rng, -spreadX, spreadX);
      const startY = y + randomRange(rng, -spreadY, spreadY);
      // Крупный сгусток тяжелее: высоту берет из нижней доли размаха высот
      const riseShare = rng() * lerp(1, clots.heavyRiseShare, sizeShare);
      const rise = Math.max(0, Math.min(
        startY - ceilingY, hexSize * scale * lerp(look.riseFactor.min, look.riseFactor.max, riseShare)
      ));
      // Высота задает и скорость вылета, и время до вершины: тяготение одно на всех
      const launchSpeed = Math.sqrt(2 * gravity * rise);
      const apexMs = launchSpeed / gravity;
      // Вниз сгусток пролетает долю подъема: при постоянном тяготении путь растет как квадрат времени
      const fallMs = apexMs * Math.sqrt(randomIn(rng, clots.fallShare));
      // Веер вокруг «вверх по экрану»; ветер матча клонит его едва заметно
      const fanRad = toRadians(look.fanDeg) * randomRange(rng, -1, 1);
      const velocityX = launchSpeed * (Math.tan(fanRad) + clots.windLeanShare * wind.x);
      const flight = {
        x: startX,
        y: startY,
        // Выброс — рывком: сгустки вылетают почти разом
        bornMs: startMs + rng() * clots.volleyMs,
        launchSpeed,
        velocityX,
        // Скорость вылета: по ней меряется, насколько капля вытянута
        speed: Math.hypot(launchSpeed, velocityX),
        apexMs,
        fallMs,
        lifeMs: apexMs + fallMs,
        rise,
        size: hexSize * scale * lerp(look.sizeFactor.min, look.sizeFactor.max, sizeShare),
      };
      particles.push({
        kind: CLOT_BODY,
        layer: look.bodyLayer,
        sublayer: CLOT_BODY_SUBLAYER,
        texture: FIELD_BIOME_TEXTURES.CLOT,
        tint: clots.tints.hot,
        flight,
      }, {
        kind: CLOT_HALO,
        layer: look.haloLayer,
        sublayer: CLOT_HALO_SUBLAYER,
        texture: FIELD_BIOME_TEXTURES.DOT,
        tint: clots.tints.hot,
        flight,
      });
      endMs = Math.max(endMs, flight.bornMs + flight.lifeMs);
    }
    return {particles, endMs};
  };

  const resolveClotPose = (particle, elapsedMs, out) => {
    const {flight} = particle;
    const ageMs = elapsedMs - flight.bornMs;
    if (!(ageMs > 0) || !(ageMs < flight.lifeMs)) {
      return writeHiddenPose(out);
    }
    // Бросок камня: вбок — ровно, по вертикали — рывок вверх против постоянного тяготения
    const x = flight.x + flight.velocityX * ageMs;
    const y = flight.y - flight.launchSpeed * ageMs + (gravity * ageMs * ageMs) / 2;
    const riseShare = Math.min(1, ageMs / flight.apexMs);
    const fallShare = Math.max(0, (ageMs - flight.apexMs) / flight.fallMs);
    const tint = resolveClotTint(clots, riseShare, fallShare);

    if (particle.kind === CLOT_HALO) {
      // Накал держится в начале подъема и гаснет к мигу, когда капля застыла
      const heat = 1 - smoothstep(
        flight.apexMs * clots.halo.coolFrom, flight.apexMs + flight.fallMs * clots.solidAt, ageMs
      );
      const size = flight.size * clots.halo.sizeScale * lerp(clots.halo.endScale, 1, heat);
      writePose(out, x, y, clots.halo.alpha * heat, size, size, 0);
      // Светящийся слой темную краску не покажет: ореол остывает до красного и гаснет
      out.tint = fallShare > 0 ? clots.tints.red : tint;
      return out;
    }

    // Капля вытянута вдоль скорости и повернута по ней; в вершине, где скорость мала, — круглая.
    // Другого вращения у нее нет: сгусток не кувыркается
    const velocityY = gravity * ageMs - flight.launchSpeed;
    const speedShare = Math.min(1, Math.hypot(flight.velocityX, velocityY) / flight.speed);
    writePose(
      out, x, y,
      // Тает только застывшая капля
      clots.alpha * (1 - smoothstep(clots.fadeFrom, 1, fallShare)),
      flight.size * (1 + clots.stretch.along * speedShare),
      flight.size * (1 - clots.stretch.across * speedShare),
      Math.atan2(velocityY, flight.velocityX)
    );
    out.tint = tint;
    return out;
  };

  /** Вспышка жерла на залпе: мягкое светящееся пятно на земле */
  const buildFlare = (x, y, size, look) => ({
    kind: FLARE,
    layer: GROUND_GLOW_LAYER,
    sublayer: CORE_SUBLAYER,
    texture: FIELD_BIOME_TEXTURES.DOT,
    tint: look.tint,
    x,
    y,
    size,
    alpha: look.alpha,
    startMs: 0,
    riseMs: look.riseMs,
    decayMs: look.decayMs,
    startScale: look.startScale,
    flatten: look.flatten,
  });

  // --------------------------------------------------------------- волна жара

  // Размер пятна жара: доля стороны картинки в пределах, заданных в ячейках
  const resolveHeatSize = ({sideShare, sizeFactor}) => (
    Math.min(hexSize * sizeFactor.max, Math.max(hexSize * sizeFactor.min, side * sideShare))
  );
  const riverSize = resolveHeatSize(glow.river);
  const wideSize = resolveHeatSize(glow.wide);
  const splashCeilingY = context.toWorld(0, splashes.ceilingV).y;

  /**
   * Лежит ли место всплеска под игровыми ячейками. Место — точка с разбросом мест вылета, ячейка — круг ее
   * описанной окружности. Спрашивается у самих ячеек: карта покрытия поля размыта на несколько ячеек, и якорь
   * под угловой ячейкой поля по ней не отличить от якоря, до которого от края поля — целая ячейка.
   */
  const checkIfUnderCells = ({x, y}, spreadX, spreadY) => {
    const {cells, fieldBox} = context;
    // Почти вся лава — за рамкой игровых ячеек: там перебирать ячейки незачем
    const isInFieldBox = x + spreadX >= fieldBox.left && x - spreadX <= fieldBox.right
      && y + spreadY >= fieldBox.top && y - spreadY <= fieldBox.bottom;
    return isInFieldBox && cells.some((cell) => {
      // От края места до центра ячейки: по каждой оси — расстояние за вычетом разброса
      const gapX = Math.max(0, Math.abs(cell.x - x) - spreadX);
      const gapY = Math.max(0, Math.abs(cell.y - y) - spreadY);
      return gapX * gapX + gapY * gapY <= hexSize * hexSize;
    });
  };

  // Русла в координатах мира
  const chains = params.chains.map(({id, anchors}, chainIndex) => ({
    id,
    chainIndex,
    anchors: anchors.map((anchor) => {
      const isWide = anchor.radius >= glow.wideRadius;
      const place = context.toWorld(anchor.u, anchor.v);
      // Сгустки вылетают из лавы: в пределах ее ширины у якоря; по вертикали разброс меньше
      const spreadX = Math.max(anchor.radius * side, hexSize * splashes.minSpreadFactor);
      const spreadY = spreadX * splashes.spreadFlatten;
      return {
        ...place,
        isVent: Boolean(anchor.isVent),
        size: isWide ? wideSize : riverSize,
        tint: isWide ? glow.wide.tint : glow.river.tint,
        spreadX,
        spreadY,
        // Плюется ли якорь вообще (решается раз: пока эффект жив, поле то же). Выше линии дали сгусткам
        // нельзя: якорю под самой линией их не достается. И лаве под игровыми ячейками — тоже. Картинка
        // пейзажа вдвое больше игровой части поля, и почти на каждом поле его край ложится на нарисованные
        // русла; сгустки летят над жетонами и из-под рудника или флага вылетали бы, как из самого строения.
        // Жар такого якоря остается: он на земле, под жетонами
        canSplash: place.y - splashCeilingY >= hexSize * splashes.minRiseFactor
          && !checkIfUnderCells(place, spreadX, spreadY),
      };
    }),
  }));

  /** Всплеск якоря на гребне волны; room — сколько сгустков еще можно участку русла */
  const buildSplash = (rng, anchor, crestMs, room) => {
    const wanted = anchor.isVent
      ? randomInt(rng, splashes.ventCount.min, splashes.ventCount.max)
      : (rng() < splashes.spitChance ? randomInt(rng, splashes.count.min, splashes.count.max) : 0);
    // Жерла и озера плюются всегда и сполна: потолок режет только участки русел
    const count = anchor.isVent ? wanted : Math.min(wanted, room);
    return buildClots(rng, {
      x: anchor.x,
      y: anchor.y,
      look: splashes,
      count: anchor.canSplash ? count : 0,
      startMs: crestMs,
      ceilingY: splashCeilingY,
      spreadX: anchor.spreadX,
      spreadY: anchor.spreadY,
    });
  };

  const waveTrack = {
    schedule: {firstDelayMs: params.firstDelayMs, intervalMs: params.intervalMs, maxGroups: params.maxGroups},
    buildGroup: ({rng, view, liveGroups}) => {
      // Русло, которое уже горит, второй волной не накрывается: два пятна разом
      // выжгли бы лаву добела
      const burning = new Set(liveGroups.map((group) => group.chainIndex));
      const lookBox = view ? expandBox(view, hexSize * params.viewMarginFactor) : null;
      const chain = pickWeighted(rng, chains.filter(({chainIndex}) => !burning.has(chainIndex)), ({anchors}) => (
        lookBox && anchors.some((anchor) => checkIfPointInBox(anchor, lookBox)) ? params.inViewWeight : 1
      ));
      if (!chain) {
        return null;
      }

      // Сколько сгустков еще можно участкам этого русла: потолок минус заявленное живыми волнами. Волна держит
      // за собой все свои сгустки, пока жива: какие из них еще летят, отсюда не видно — у живых групп нет возраста
      let clotRoom = Math.max(0, splashes.maxLive - liveGroups.reduce((sum, group) => sum + group.clotCount, 0));
      const spots = [];
      const flying = [];
      let durationMs = 0;
      chain.anchors.forEach((anchor, index) => {
        const startMs = index * pulse.stepMs;
        spots.push({
          kind: HEAT,
          layer: GROUND_GLOW_LAYER,
          texture: FIELD_BIOME_TEXTURES.DOT,
          tint: anchor.tint,
          x: anchor.x,
          y: anchor.y,
          size: anchor.size,
          startMs,
        });
        durationMs = Math.max(durationMs, startMs + pulse.glowMs);
        // Всплеск — на гребне волны этого участка
        const splash = buildSplash(rng, anchor, startMs + pulse.glowMs / 2, clotRoom);
        flying.push(...splash.particles);
        // Сгустки жерла тоже заявлены, но места им не нужно: меньше нуля запас не становится
        clotRoom = Math.max(0, clotRoom - splash.particles.length / 2);
        durationMs = Math.max(durationMs, splash.endMs);
      });

      return {
        durationMs,
        chainIndex: chain.chainIndex,
        chainId: chain.id,
        clotCount: flying.length / 2,
        particles: [...spots, ...flying],
      };
    },
    resolvePose: (group, particle, elapsedMs, out) => {
      if (particle.kind !== HEAT) {
        return resolveClotPose(particle, elapsedMs, out);
      }
      const ageMs = elapsedMs - particle.startMs;
      if (!(ageMs > 0) || !(ageMs < pulse.glowMs)) {
        return writeHiddenPose(out);
      }
      // Одна плавная волна: 0 -> гребень -> 0
      const wave = Math.sin((Math.PI * ageMs) / pulse.glowMs);
      const size = particle.size * lerp(pulse.swellFrom, 1, wave);
      return writePose(out, particle.x, particle.y, pulse.peakAlpha * wave, size, size, 0);
    },
  };

  // ------------------------------------------------------------------ вулканы

  const {eruptions, smoke} = craters;
  // Кратеры в координатах мира
  const craterSites = params.craterList.map((crater) => {
    const from = context.toWorld(crater.smoke.from.u, crater.smoke.from.v);
    const to = context.toWorld(crater.smoke.to.u, crater.smoke.to.v);
    return {
      ...context.toWorld(crater.u, crater.v),
      id: crater.id,
      scale: crater.scale,
      smoke: {x: from.x, y: from.y, riseX: to.x - from.x, riseY: to.y - from.y, density: crater.smoke.density},
      canErupt: crater.volleyCeilingV !== null,
      ceilingY: crater.volleyCeilingV === null ? 0 : context.toWorld(0, crater.volleyCeilingV).y,
    };
  });

  const eruptionTrack = {
    schedule: {
      firstDelayMs: eruptions.firstDelayMs, intervalMs: eruptions.intervalMs, maxGroups: eruptions.maxGroups,
    },
    buildGroup: ({rng, view, liveGroups}) => {
      // Кратер, залп которого еще летит (или который после залпа молчит), второй раз не бьет
      const erupting = new Set(liveGroups.map((group) => group.craterId));
      const lookBox = view ? expandBox(view, hexSize * params.viewMarginFactor) : null;
      const ready = craterSites.filter(({id, canErupt}) => canErupt && !erupting.has(id));
      const site = pickWeighted(rng, ready, (crater) => (
        lookBox && checkIfPointInBox(crater, lookBox) ? params.inViewWeight : 1
      ));
      if (!site) {
        return null;
      }
      const flare = buildFlare(site.x, site.y, side * eruptions.flare.sizeShare * site.scale, eruptions.flare);
      const mouth = side * eruptions.mouthShare * site.scale;
      const volley = buildClots(rng, {
        x: site.x,
        y: site.y,
        look: eruptions,
        count: randomInt(rng, eruptions.count.min, eruptions.count.max),
        startMs: 0,
        ceilingY: site.ceilingY,
        scale: site.scale,
        spreadX: mouth,
        // Устье нарисовано сбоку и сплюснуто
        spreadY: mouth * splashes.spreadFlatten,
      });
      return {
        durationMs: Math.max(volley.endMs, flare.riseMs + flare.decayMs) + randomIn(rng, eruptions.restMs),
        craterId: site.id,
        clotCount: volley.particles.length / 2,
        particles: [flare, ...volley.particles],
      };
    },
    resolvePose: (group, particle, elapsedMs, out) => (
      particle.kind === FLARE ? resolveFlarePose(particle, elapsedMs, out) : resolveClotPose(particle, elapsedMs, out)
    ),
  };

  const smokeTrack = {
    schedule: {
      firstDelayMs: smoke.firstDelayMs, intervalMs: smoke.intervalMs, maxGroups: smoke.maxGroups,
      prewarm: smoke.prewarm,
    },
    buildGroup: ({rng, liveGroups}) => {
      // Клуб — кратеру, которому сейчас дыма недостает больше всех (по его доле); равным — жребий
      const counts = {};
      liveGroups.forEach(({craterId}) => {
        counts[craterId] = (counts[craterId] || 0) + 1;
      });
      const resolveFill = ({id, smoke: {density}}) => (counts[id] || 0) / density;
      const leastFill = Math.min(...craterSites.map(resolveFill));
      const site = pick(rng, craterSites.filter((crater) => resolveFill(crater) <= leastFill + 1e-9));
      // У подложки без кратеров дыму взяться неоткуда
      if (!site) {
        return null;
      }
      // Сдвиг клуба от оси столба: случайная точка круга
      const scatterAngle = rng() * TWO_PI;
      const scatterDistance = Math.sqrt(rng());
      const sizeScale = site.scale * randomIn(rng, smoke.sizeJitter);
      const lifeMs = randomIn(rng, smoke.lifeMs);
      return {
        durationMs: lifeMs,
        craterId: site.id,
        particles: [{
          kind: SMOKE,
          layer: GROUND_LAYER,
          texture: FIELD_BIOME_TEXTURES.PUFF,
          tint: smoke.tints.young,
          x: site.smoke.x,
          y: site.smoke.y,
          riseX: site.smoke.riseX,
          riseY: site.smoke.riseY,
          // Ветер матча клонит столб: чем выше клуб, тем дальше его снесло
          leanX: Math.abs(site.smoke.riseY) * smoke.windLeanShare * wind.x,
          lifeMs,
          sizeFrom: side * smoke.sizeShare.from * sizeScale,
          sizeTo: side * smoke.sizeShare.to * sizeScale,
          scatterX: Math.cos(scatterAngle) * scatterDistance * side * site.scale,
          scatterY: Math.sin(scatterAngle) * scatterDistance * side * site.scale,
          startRad: rng() * TWO_PI,
          spinRadPerMs: (toRadians(smoke.spinDegPerSecond) / 1000) * randomRange(rng, 0.3, 1) * randomSign(rng),
        }],
      };
    },
    resolvePose: (group, particle, elapsedMs, out) => {
      const life = elapsedMs / particle.lifeMs;
      if (!(life > 0) || !(life < 1)) {
        return writeHiddenPose(out);
      }
      // Быстро из жерла, дальше — все медленнее
      const progress = 1 - (1 - life) ** smoke.progressPower;
      const scatter = lerp(smoke.scatterShare.from, smoke.scatterShare.to, progress);
      const size = lerp(particle.sizeFrom, particle.sizeTo, progress);
      writePose(
        out,
        particle.x + particle.riseX * progress + particle.leanX * progress * progress + particle.scatterX * scatter,
        particle.y + particle.riseY * progress + particle.scatterY * scatter,
        smoke.alpha * smoothstep(0, smoke.fadeInShare, life) * (1 - life) ** smoke.fadePower,
        size, size,
        particle.startRad + particle.spinRadPerMs * elapsedMs
      );
      // У жерла дым почти черный, выше сереет
      const {young, old, turnFrom, turnTo} = smoke.tints;
      out.tint = mixTints(young, old, smoothstep(turnFrom, turnTo, life));
      return out;
    },
  };

  const craterGlowTrack = {
    // Свет жерла ветра не слушает: при смене ветра на старте партии он не перестраивается
    usesWind: false,
    schedule: {standing: true},
    buildGroup: ({rng}) => ({
      durationMs: Infinity,
      particles: craterSites.flatMap((site) => {
        // Зарево и ядро одного кратера дышат в такт, разные кратеры — вразнобой
        const beat = {
          breathMs: randomIn(rng, craters.glow.breathMs), phase: rng() * TWO_PI, secondPhase: rng() * TWO_PI,
        };
        return [craters.glow.wide, craters.glow.core].map((look, sublayer) => ({
          kind: CRATER_GLOW,
          layer: GROUND_GLOW_LAYER,
          sublayer,
          texture: FIELD_BIOME_TEXTURES.DOT,
          tint: look.tint,
          x: site.x,
          y: site.y,
          craterId: site.id,
          size: side * look.sizeShare * site.scale,
          flatten: look.flatten,
          alpha: look.alpha,
          ...beat,
        }));
      }),
    }),
    resolvePose: (group, particle, elapsedMs, out) => {
      // Неровное дыхание: две волны с несовпадающими периодами
      const turn = (TWO_PI * elapsedMs) / particle.breathMs;
      const breath = 0.5 + 0.5 * (
        (1 - craters.glow.secondMix) * Math.sin(turn + particle.phase)
        + craters.glow.secondMix * Math.sin(turn / craters.glow.secondPeriodShare + particle.secondPhase)
      );
      const size = particle.size * lerp(craters.glow.swellFrom, 1, breath);
      return writePose(
        out, particle.x, particle.y, lerp(particle.alpha.min, particle.alpha.max, breath),
        size, size * particle.flatten, 0
      );
    },
  };

  // --------------------------------------------------------------------- горы

  /** Гора для новых огоньков: та, что сейчас не дышит, охотнее — та, что в кадре; undefined — гор нет */
  const pickMountain = (rng, view, liveGroups) => {
    const busy = new Set(liveGroups.map((group) => group.mountainKey));
    const lookBox = view ? expandBox(view, hexSize * mountains.viewMarginFactor) : null;
    // Горы — нынешние: список один на все время жизни эффекта, а его содержимое меняется (редактор поля)
    const free = context.mountains.filter((mountain) => !busy.has(resolveMountainKey(mountain)));
    return pickWeighted(rng, free, (mountain) => (
      lookBox && checkIfPointInBox(mountain, lookBox) ? mountains.inViewWeight : 1
    ));
  };

  const mountainGlowTrack = {
    // Огоньки стоят на жетонах: ветер им не нужен
    usesWind: false,
    schedule: {
      firstDelayMs: mountains.glow.firstDelayMs, intervalMs: mountains.glow.intervalMs,
      maxGroups: mountains.glow.maxGroups, prewarm: mountains.glow.prewarm,
    },
    buildGroup: ({rng, view, liveGroups}) => {
      const mountain = pickMountain(rng, view, liveGroups);
      if (!mountain) {
        return null;
      }
      // Светится часть мест магмы, каждый раз своя: первые места перетасованного списка
      const spots = [...params.mountainSpots];
      const {min, max} = mountains.glow.spotCount;
      const spotCount = Math.min(spots.length, randomInt(rng, min, max));
      for (let index = 0; index < spotCount; index += 1) {
        const swapIndex = index + Math.min(spots.length - index - 1, Math.floor(rng() * (spots.length - index)));
        [spots[index], spots[swapIndex]] = [spots[swapIndex], spots[index]];
      }
      // У каждой горы свой ритм и своя фаза: горы дышат вразнобой
      const breathMs = randomIn(rng, mountains.glow.breathMs);
      const phase = rng() * TWO_PI;
      return {
        durationMs: randomIn(rng, mountains.glow.lifeMs),
        mountainKey: resolveMountainKey(mountain),
        breathMs,
        phase,
        particles: spots.slice(0, spotCount).flatMap((spot) => {
          // Огоньки одной горы — чуть вразнобой; ореол и сердцевина одного огонька — в такт
          const spotPhase = phase + randomRange(rng, -1, 1) * mountains.glow.spreadShare * TWO_PI;
          return GLOW_PARTS.map((part, sublayer) => ({
            kind: MOUNTAIN_GLOW,
            part,
            layer: mountains.glow.layer,
            sublayer,
            texture: FIELD_BIOME_TEXTURES.DOT,
            tint: mountains.glow[part].tint,
            spotId: spot.id,
            x: mountain.x + spot.dx * hexSize,
            y: mountain.y + spot.dy * hexSize,
            size: hexSize * mountains.glow[part].sizeFactor * spot.size,
            phase: spotPhase,
          }));
        }),
      };
    },
    resolvePose: (group, particle, elapsedMs, out) => {
      const envelope = resolveLifeEnvelope(
        elapsedMs / group.durationMs, mountains.glow.fadeInShare, mountains.glow.fadeOutShare
      );
      if (!(envelope > 0)) {
        return writeHiddenPose(out);
      }
      const breath = 0.5 + 0.5 * Math.sin((TWO_PI * elapsedMs) / group.breathMs + particle.phase);
      const size = particle.size * lerp(mountains.glow.swellFrom, 1, breath);
      return writePose(
        out, particle.x, particle.y,
        mountains.glow[particle.part].alpha * envelope * lerp(mountains.glow.breathFloor, 1, breath), size, size, 0
      );
    },
  };

  return [waveTrack, eruptionTrack, smokeTrack, craterGlowTrack, mountainGlowTrack];
};

const livingLava = {
  kind: LIVING_LAVA_KIND,
  textures: [FIELD_BIOME_TEXTURES.DOT, FIELD_BIOME_TEXTURES.CLOT, FIELD_BIOME_TEXTURES.PUFF],
  createTracks,
};

export default livingLava;
